import {
  Checkbox,
  focusRing,
  FormControl,
  GridFilterInputValueProps,
  GridFilterOperator,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@platform/style";
import * as React from "react";

type statusOptions = { value: string; label: string };
type resources = { value: string; label: string };

function formatStatus(value: string): string {
  let formatted: string = value
    .split(",")
    .map((status) =>
      status
        .trim()
        .toLowerCase()
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase())
    )
    .join(", ");
  return `${formatted}`;
}

function getResourceLabels(value: string, valueOptions: resources[]): string {
  let resourceNames = valueOptions.filter((names) =>
    value
      .split(",")
      .map((name) => name.trim())
      .includes(names.value)
  );
  return resourceNames.map((name) => name.label).join(", ");
}

export const MultiSelectOperator = (
  valueOptions: string[] | statusOptions[] | resources[],
  inputLabel: string
): GridFilterOperator => ({
  label: "is",
  value: "is",
  getApplyFilterFn: (filterItem) => {
    if (!filterItem.field || !filterItem.value || !filterItem.operator) {
      return null;
    }

    return (params: any): boolean => {
      return String(params.value) >= String(filterItem.value);
    };
  },
  getValueAsString: (value: string): string => {
    switch (inputLabel) {
      case "Status":
        return formatStatus(value);
      case "Assigned To":
      case "Closed By":
        return getResourceLabels(value, valueOptions as resources[]);
      default:
        return `${value}`;
    }
  },
  InputComponent: (props: GridFilterInputValueProps) => {
    const [values, setValues] = React.useState<string[]>([]);
    const [selectedLabels, setSelectedLabels] = React.useState<string[]>([]);
    const { item, applyValue } = props;
    const ITEM_HEIGHT = 48;
    const ITEM_PADDING_TOP = 8;

    React.useEffect(() => {
      if (item.value) {
        const initialValues = item.value.split(", ");
        const derivedValues = getDropdownValues(initialValues, valueOptions);
        setValues(derivedValues.map((option) => option.value));
        setSelectedLabels(derivedValues.map((option) => option.label));
      }
    }, [item.value]);

    const handleChange = (event: SelectChangeEvent<typeof values>) => {
      const {
        target: { value },
      } = event;
      const selectedValues = typeof value === "string" ? value.split(",") : value;
      const selectedOptions = getDropdownValues(selectedValues, valueOptions);
      setValues(selectedOptions.map((option) => option.value));
      setSelectedLabels(selectedOptions.map((option) => option.label));
    };

    function getDropdownValues(
      selectedValues: string[],
      valueOptions: string[] | statusOptions[]
    ): { value: string; label: string }[] {
      return selectedValues
        .map((selectedValue) => {
          let matchedOption: string | statusOptions | undefined;
          if (typeof valueOptions[0] === "string") {
            matchedOption = (valueOptions as string[]).find((option) => option === selectedValue);
            return { value: matchedOption || "", label: matchedOption || "" };
          } else {
            matchedOption = (valueOptions as statusOptions[]).find(
              (option) => option.value === selectedValue || option.label === selectedValue
            );
            return { value: matchedOption?.value || "", label: matchedOption?.label || "" };
          }
        })
        .filter((option) => option.value && option.label);
    }

    return (
      <div>
        <FormControl sx={{ m: 1, width: 180 }}>
          <InputLabel id={inputLabel}>{inputLabel}</InputLabel>
          <Select
            SelectDisplayProps={{ style: { paddingTop: 7, paddingBottom: 7 } }}
            multiple
            aria-label={inputLabel}
            value={values}
            onChange={handleChange}
            sx={{
              marginTop: "5px",
            }}
            renderValue={() => selectedLabels.join(", ")}
            onBlur={() => {
              const dropdownValues = getDropdownValues(values, valueOptions);
              applyValue({
                ...item,
                value: dropdownValues.map((option) => option.value).join(", "),
              });
              setValues(dropdownValues.map((option) => option.value));
              setSelectedLabels(dropdownValues.map((option) => option.label));
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                  width: 300,
                  wordWrap: "break-word",
                },
              },
            }}
          >
            {valueOptions.map((option, index) => {
              if (typeof option === "string") {
                return (
                  <MenuItem
                    key={option}
                    value={option}
                    sx={{
                      focusRing,
                    }}
                    style={{ whiteSpace: "normal", wordBreak: "break-word" }}
                  >
                    <Checkbox checked={values.includes(option)} />
                    <ListItemText primary={option} />
                  </MenuItem>
                );
              } else if (typeof option === "object") {
                return (
                  <MenuItem key={index} value={option.value} sx={focusRing}>
                    <Checkbox checked={values.includes(option.value)} />
                    <ListItemText primary={option.label} />
                  </MenuItem>
                );
              }
            })}
          </Select>
        </FormControl>
      </div>
    );
  },
});
